import { createBobArt } from './148-bob-art.mjs';
import { createBobRewardEffects } from './145-bob-reward-effects.mjs';
import { createBobCameraMotion } from './143-bob-camera.mjs';
import { createBobWorldEffects } from './87-bob-world-effects.mjs';
import { bobLanding } from '../../../packages/domain/src/72-bob-mastery.mjs';
import { claimGameRenderer } from './38-game-materials.mjs';
import { bobMeshFactory } from './75-bob-meshes.mjs';
import { createBobAtmosphere } from './77-bob-atmosphere.mjs';
import { createBobMotion } from './78-bob-motion.mjs';
import { createBobCourse } from './79-bob-course.mjs';
import { getBobLevel } from '../../../packages/domain/src/67-bob-levels.mjs';
import { BOB_PHYSICS } from '../../../packages/domain/src/68-bob-bounce.mjs';

let threePromise;
const defaultThree = () =>
  (threePromise ??= import('three').catch((error) => {
    // A transient chunk download must not poison every later explicit retry.
    threePromise = null;
    throw error;
  }));
const PALETTES = [
  ['#8be1ff', '#ff9c54', '#fa6898'],
  ['#afdffa', '#71caed', '#a28bf9'],
  ['#b4edc1', '#38b77a', '#ffc65c'],
  ['#ffd1f0', '#ec73b7', '#8f8aff'],
  ['#ffe4ac', '#f5ad52', '#c26770'],
  ['#9cecec', '#ffac87', '#3abfbc'],
  ['#ffc6b5', '#b96e72', '#ff9844'],
  ['#c6e7ff', '#b28ceb', '#ffd27e'],
  ['#88dcdf', '#3fb6c2', '#f399c0'],
  ['#222d61', '#8264d9', '#71d9f5'],
  ['#473d81', '#de66d3', '#67e9ef'],
  ['#ffdde8', '#dd8cae', '#77caaa'],
  ['#a1d5ef', '#8584d4', '#57e4d1'],
  ['#ffddab', '#d69b68', '#ca6270'],
  ['#d7e9ff', '#eea453', '#6dc6b4'],
  ['#b5dec6', '#68a76e', '#dcb978'],
  ['#c8c8ed', '#9982c5', '#f6cde0'],
  ['#b8edf6', '#59c8ce', '#df93e8'],
  ['#95cce0', '#78bacd', '#b193e5'],
  ['#c3d0f4', '#ccb37b', '#dfa2df'],
];

/** Resolve only an authored course's original optimized background for previews. */
export function getBobBackdropUrl(level) {
  const id = getBobLevel(level).id;
  return new URL(`../assets/game-bob-${String(id).padStart(2, '0')}-v1-640.webp`, import.meta.url)
    .href;
}

/** Create one real Three.js scene, without a timer or simulation. dt is seconds;
 * draw receives canonical immutable state. The controller owns visibility/RAF.
 * Failed imports/context/setup return null and report a closed reason. Disposal
 * cancels ownership and stale texture callbacks; replacement never auto-retries. */
export async function createBobRenderer({
  canvas,
  onUnavailable = () => {},
  onInvalidate = () => {},
  onArtStatus = () => {},
  reducedMotion = false,
  canInitialize = () => true,
  loadThree = defaultThree,
  createArt = createBobArt,
  createCrateArt,
  createRenderer = (THREE, options) => new THREE.WebGLRenderer(options),
  loadBackdrop = (THREE, url, ok, fail) => new THREE.TextureLoader().load(url, ok, undefined, fail),
} = {}) {
  let THREE;
  try {
    THREE = await loadThree();
  } catch {
    onUnavailable('import');
    return null;
  }
  // A hidden or revoked pending card must not evict another game's GPU lease.
  if (!canInitialize()) {
    onUnavailable('cancelled');
    return null;
  }
  if (!canvas || canvas.isConnected === false) {
    onUnavailable('disconnected');
    return null;
  }
  let renderer, scene, camera, world, factory, bob, finish, key, groundShadow, landingRing;
  const atmosphere = createBobAtmosphere(THREE);
  let pixelRatio = 1,
    sceneViewport = null;
  let backdrop,
    backdropTexture,
    pendingTexture,
    textureRequest = 0,
    currentLevel = null,
    worldVersion = 0;
  let disposed = false,
    releaseLease,
    width = 1,
    height = 1;
  let initializedCamera = false,
    lastEvent = 0,
    clock = 0,
    lastState,
    finishCameraOrigin = null,
    introCameraOrigin = null;
  let trail = [];
  let motion, courseWindow, worldEffects, rewardEffects, art;
  const cameraMotion = createBobCameraMotion();
  const disposedTextures = new WeakSet();
  let backdropReady = Promise.resolve(false),
    settleBackdrop = () => {},
    backdropTimer = null;
  function disposeTexture(texture) {
    if (!texture || disposedTextures.has(texture)) return;
    disposedTextures.add(texture);
    texture.dispose();
  }
  const report = (reason) => {
    if (disposed) return;
    dispose();
    onUnavailable(reason);
  };
  function contextAvailable() {
    // Three can silently skip rendering while the context-loss event is queued.
    if (!renderer?.getContext?.()?.isContextLost?.()) return true;
    report('context');
    return false;
  }
  function clearWorld() {
    settleBackdrop(false);
    clearTimeout(backdropTimer);
    backdropTimer = null;
    textureRequest++;
    atmosphere.setBackdrop(null);
    disposeTexture(backdropTexture);
    disposeTexture(pendingTexture);
    pendingTexture = null;
    backdropTexture = null;
    rewardEffects?.clear();
    art?.dispose();
    art = null;
    factory?.dispose();
    if (world) scene.remove(world);
    courseWindow?.dispose();
    courseWindow = null;
    trail = [];
  }
  function dispose() {
    if (disposed) return;
    disposed = true;
    canvas?.removeEventListener('webglcontextlost', lost);
    clearWorld();
    atmosphere.dispose();
    key?.shadow?.map?.dispose();
    renderer?.dispose();
    renderer?.forceContextLoss?.();
    releaseLease?.();
    if (canvas?.dataset) canvas.dataset.graphics = 'disposed';
  }
  const lost = (event) => {
    event.preventDefault();
    report('context');
  };
  try {
    releaseLease = claimGameRenderer((reason) => report(reason));
    renderer = createRenderer(THREE, {
      canvas,
      antialias: true,
      alpha: false,
      powerPreference: 'default',
    });
    if (!contextAvailable()) return null;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.18;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(48, 1, 0.1, 180);
    scene.add(new THREE.HemisphereLight('#ffffff', '#625078', 2.1));
    key = new THREE.DirectionalLight('#fff5d8', 3.3);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    Object.assign(key.shadow.camera, {
      left: -12,
      right: 12,
      top: 18,
      bottom: -12,
      near: 0.5,
      far: 65,
    });
    key.shadow.camera.updateProjectionMatrix();
    key.shadow.bias = -0.0007;
    key.shadow.normalBias = 0.035;
    scene.add(key, key.target);
    const rim = new THREE.DirectionalLight('#c0ecff', 2.2);
    rim.position.set(7, 5, 10);
    scene.add(rim);
    canvas.addEventListener('webglcontextlost', lost);
    if (canvas.dataset) canvas.dataset.graphics = 'ready';
  } catch {
    report('setup');
    return null;
  }
  function createWorld(level) {
    clearWorld();
    worldVersion++;
    currentLevel = level.id;
    lastEvent = 0;
    initializedCamera = false;
    finishCameraOrigin = introCameraOrigin = null;
    backdropReady = new Promise((resolve) => {
      settleBackdrop = resolve;
    });
    factory = bobMeshFactory(THREE, { prepareMaterial: atmosphere.prepare });
    rewardEffects = createBobRewardEffects({
      THREE,
      canvas,
      scene,
      factory,
      prepare: atmosphere.prepare,
    });
    world = new THREE.Group();
    scene.add(world);
    const palette = PALETTES[Math.max(0, Math.min(19, Number(level.id) - 1))];
    scene.background = new THREE.Color(palette[0]);
    scene.fog = new THREE.Fog(palette[0], 30, 82);
    groundShadow = new THREE.Mesh(
      factory.keep(new THREE.CircleGeometry(1, 32)),
      factory.keep(
        new THREE.MeshBasicMaterial({
          color: '#233a54',
          transparent: true,
          opacity: 0.24,
          depthWrite: false,
        }),
      ),
    );
    groundShadow.name = 'Bob ground shadow';
    landingRing = factory.torus(world, '#fff6a5', [0, 0.06, 0], 0.6, 0.3);
    landingRing.name = 'Predicted landing';
    landingRing.rotation.x = Math.PI / 2;
    landingRing.castShadow = false;
    groundShadow.rotation.x = -Math.PI / 2;
    world.add(groundShadow);
    bob = factory.character();
    bob.root.scale.setScalar(0.72);
    world.add(bob.root);
    motion = createBobMotion({ bob, groundShadow });
    art = createArt({
      THREE,
      bob,
      prepareMaterial: atmosphere.prepare,
      onInvalidate: () => {
        if (!disposed && currentLevel === level.id) onInvalidate();
      },
      onStatus: (status) => {
        if (!disposed) {
          if (canvas.dataset) canvas.dataset.bobArt = status;
          onArtStatus(status);
        }
      },
    });
    worldEffects = createBobWorldEffects(THREE, factory, world, level);
    finish = factory.finish(level.finish.radius * 2);
    finish.group.position.set(level.finish.x, level.finish.y, level.finish.z);
    world.add(finish.group);
    courseWindow = createBobCourse(THREE, {
      scene,
      level,
      palette,
      prepareMaterial: atmosphere.prepare,
      onInvalidate,
      createCrateArt,
    });
    for (let i = 0; i < 10; i++) {
      const mesh = factory.ball(world, '#fff1a4', [0, -100, 0], [0.08, 0.08, 0.08], 0.55);
      mesh.castShadow = false;
      trail.push(mesh);
    }
    const geometry = factory.keep(new THREE.PlaneGeometry(1, 1));
    const material = factory.keep(
      new THREE.MeshBasicMaterial({ color: palette[0], fog: false, depthWrite: false }),
    );
    backdrop = new THREE.Mesh(geometry, material);
    backdrop.name = 'Authored scene backdrop';
    backdrop.rotation.y = Math.PI;
    backdrop.renderOrder = -10;
    world.add(backdrop);
    const request = ++textureRequest;
    const url = getBobBackdropUrl(level.id);
    if (canvas.dataset) canvas.dataset.backdrop = 'loading';
    try {
      const requested = loadBackdrop(
        THREE,
        url,
        (texture) => {
          if (disposed || request !== textureRequest) {
            disposeTexture(texture);
            return;
          }
          pendingTexture = null;
          backdropTexture = texture;
          atmosphere.setBackdrop(texture);
          texture.colorSpace = THREE.SRGBColorSpace;
          backdrop.material.map = texture;
          backdrop.material.color.set('#ffffff');
          backdrop.material.needsUpdate = true;
          if (canvas.dataset) canvas.dataset.backdrop = 'ready';
          settleBackdrop(true);
          clearTimeout(backdropTimer);
          onInvalidate();
        },
        () => {
          if (!disposed && request === textureRequest && canvas.dataset)
            canvas.dataset.backdrop = 'fallback';
          if (request === textureRequest) {
            disposeTexture(pendingTexture);
            pendingTexture = null;
            settleBackdrop(false);
            clearTimeout(backdropTimer);
            if (!disposed) onInvalidate();
          }
        },
      );
      if (requested?.isTexture && !backdropTexture && !disposed && request === textureRequest)
        pendingTexture = requested;
    } catch {
      if (canvas.dataset) canvas.dataset.backdrop = 'fallback';
      settleBackdrop(false);
    }
  }
  function resize(w, h, dpr = globalThis.devicePixelRatio || 1, viewport = null) {
    if (
      disposed ||
      canvas.ownerDocument?.hidden ||
      canvas.isConnected === false ||
      !(w > 0 && h > 0)
    )
      return;
    sceneViewport =
      viewport &&
      [viewport.width, viewport.height, viewport.left, viewport.top].every(Number.isFinite) &&
      viewport.width > 0 &&
      viewport.height > 0 &&
      viewport.left >= 0 &&
      viewport.top >= 0 &&
      viewport.left + viewport.width <= w + 1 &&
      viewport.top + viewport.height <= h + 1 &&
      (viewport.width < w - 1 || viewport.height < h - 1)
        ? viewport
        : null;
    width = Math.min(4096, w);
    height = Math.min(4096, h);
    // Bound total colour/depth/MSAA allocation on large or high-density screens.
    const requestedRatio = Number.isFinite(dpr) ? Math.max(1, dpr) : 1;
    pixelRatio = Math.min(
      1.75,
      requestedRatio,
      4096 / Math.max(width, height),
      Math.sqrt(4_000_000 / (width * height)),
    );
    renderer.setPixelRatio(pixelRatio);
    atmosphere.setViewport(Math.floor(width * pixelRatio), Math.floor(height * pixelRatio));
    renderer.setSize(width, height, false);
    if (sceneViewport) {
      // Render beyond the central play frustum rather than clipping geometry at
      // HUD boundaries. The original projection lands exactly in this safe rect.
      camera.setViewOffset(
        sceneViewport.width,
        sceneViewport.height,
        -sceneViewport.left,
        -sceneViewport.top,
        width,
        height,
      );
    } else {
      camera.clearViewOffset();
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    }
  }
  function draw(state, dtSeconds = 0, reduced = reducedMotion, performance = null) {
    if (disposed || canvas.ownerDocument?.hidden || canvas.isConnected === false) return false;
    try {
      if (!contextAvailable()) return false;
      const dt = Math.min(0.05, Math.max(0, Number(dtSeconds) || 0));
      const level = getBobLevel(state.level);
      if (!factory || currentLevel !== level.id) createWorld(level);
      if (
        lastState &&
        (state.elapsed < lastState.elapsed || state.eventSerial < lastState.eventSerial)
      ) {
        lastEvent = 0;
        initializedCamera = false;
        finishCameraOrigin = null;
        rewardEffects?.clear();
      }
      lastState = state;
      clock += dt;
      const b = motion.update(state, dt, reduced);
      const introduction = ['intro', 'ready', 'countdown'].includes(performance?.phase);
      const progress = Math.max(
        0,
        Math.min(1, (performance?.seconds || 0) / (performance?.duration || 1.5)),
      );
      const smooth = (v) => v * v * (3 - 2 * v);
      const introWeight =
        performance?.phase === 'intro'
          ? smooth(Math.min(1, performance.seconds / 0.65))
          : performance?.phase === 'ready'
            ? 1
            : performance?.phase === 'countdown'
              ? 1 - smooth(progress)
              : 0;
      if (introduction)
        bob.root.rotation.y = performance.phase === 'countdown' ? Math.PI * smooth(progress) : 0;
      art?.update(state, dt, reduced, performance);
      worldEffects.update(state, reduced);
      const landing = bobLanding(state);
      landingRing.visible = state.status === 'playing' && landing.seconds < 1.8;
      landingRing.position.set(landing.x, landing.y + 0.065, landing.z);
      landingRing.scale.setScalar(0.8 + Math.min(1, landing.seconds) * 0.25);
      for (const event of state.events || [])
        if (event.id > lastEvent) {
          rewardEffects.burst(event, reduced);
          lastEvent = event.id;
        }
      rewardEffects.update(dt, reduced);
      const cameraAccent = cameraMotion.update(state, dt, reduced);
      if (camera.fov !== cameraAccent.fov) {
        camera.fov = cameraAccent.fov;
        camera.updateProjectionMatrix();
      }
      const follow = !initializedCamera || reduced ? 1 : 1 - Math.exp(-dt * 5);
      const playWidth = sceneViewport?.width || width;
      const playHeight = sceneViewport?.height || height;
      const aspectLift = Math.max(0, 1 - playWidth / playHeight) * 2;
      const target = new THREE.Vector3(
        b.x * 0.34 + cameraAccent.x,
        5.7 + Math.max(0, b.y - 2) * 0.25 + aspectLift + cameraAccent.lift,
        b.z - 10.8 - cameraAccent.distance,
      );
      if (!introduction || reduced) introCameraOrigin = null;
      if (state.status !== 'complete' || reduced) finishCameraOrigin = null;
      if (cameraAccent.finish > 0) {
        // A finite portrait of Bob replaces chase framing only after gameplay
        // has finished. Capture the start to avoid asymptotic camera drift.
        finishCameraOrigin ??= initializedCamera ? camera.position.clone() : target.clone();
        camera.position
          .copy(finishCameraOrigin)
          .lerp(new THREE.Vector3(b.x + 0.9, b.y + 1.2, b.z - 4.8), cameraAccent.finish);
      } else if (introduction && !reduced) {
        introCameraOrigin ??= initializedCamera ? camera.position.clone() : target.clone();
        camera.position
          .copy(introCameraOrigin)
          .lerp(new THREE.Vector3(b.x + 0.9, b.y + 1.2, b.z - 4.8), introWeight);
      } else camera.position.lerp(target, follow);
      // Keep Bob clear of the HUD and course menu. Continuous
      // aspect interpolation avoids a breakpoint jump when rotating/resizing.
      const portraitWeight = Math.max(0, Math.min(1, (1 - playWidth / playHeight) / 0.5));
      const shortWeight = Math.max(0, Math.min(1, (420 - playHeight) / 100));
      const lookAhead = (6 - portraitWeight * 3.6) * (1 - shortWeight) - shortWeight;
      const lookTarget = new THREE.Vector3(
        b.x * 0.35,
        1.1 + cameraAccent.lookLift,
        b.z + lookAhead,
      );
      lookTarget.lerp(
        new THREE.Vector3(b.x, b.y + 0.15, b.z),
        introduction && !reduced ? introWeight : cameraAccent.finish,
      );
      camera.lookAt(lookTarget);
      camera.rotateZ(cameraAccent.roll);
      initializedCamera = true;
      courseWindow.update(state, dt, reduced, b, camera.position);
      key.position.set(b.x - 5, 13, b.z - 7);
      key.target.position.set(b.x, 0, b.z + 4);
      // A camera-facing far plane places the artwork at the actual view centre.
      // A world-upright plane sampled only the empty bottom of portrait scenes
      // because this camera looks down the course. Cover crops preserve shape;
      // landscape crops anchor to the authored skyline instead of empty water.
      const distance = 100;
      const baseHeight = 2 * Math.tan((camera.fov * Math.PI) / 360) * distance;
      const viewHeight = (baseHeight * height) / playHeight;
      const viewDirection = camera.getWorldDirection(new THREE.Vector3());
      backdrop.position.copy(camera.position).addScaledVector(viewDirection, distance);
      if (sceneViewport) {
        const shift = new THREE.Vector3(
          (((width - playWidth) / 2 - sceneViewport.left) * baseHeight) / playHeight,
          ((sceneViewport.top - (height - playHeight) / 2) * baseHeight) / playHeight,
          0,
        ).applyQuaternion(camera.quaternion);
        backdrop.position.add(shift);
      }
      backdrop.quaternion.copy(camera.quaternion);
      backdrop.scale.set((viewHeight * width) / height, viewHeight, 1);
      if (backdropTexture) {
        const sourceAspect =
          (backdropTexture.image?.width || 640) / (backdropTexture.image?.height || 960);
        const coverAspect = width / height;
        const repeatX = Math.min(1, coverAspect / sourceAspect);
        const repeatY = Math.min(1, sourceAspect / coverAspect);
        const offsetX = (1 - repeatX) / 2;
        const offsetY = sceneViewport ? (1 - repeatY) / 2 : 1 - repeatY;
        if (backdrop.material.toneMapped !== !sceneViewport) {
          backdrop.material.toneMapped = !sceneViewport;
          backdrop.material.needsUpdate = true;
        }
        backdropTexture.repeat.set(repeatX, repeatY);
        backdropTexture.offset.set(offsetX, offsetY);
        atmosphere.setCover(repeatX, repeatY, offsetX, offsetY);
      }
      finish.halo.rotation.z = reduced ? 0 : clock * 0.3;
      for (let i = trail.length - 1; i >= 0; i--) {
        const node = trail[i];
        if (i) node.position.copy(trail[i - 1].position);
        else node.position.set(b.x, b.y - 0.2, b.z);
        const boosted = (state.effects?.boostUntil || 0) > state.elapsed;
        node.visible = !reduced && state.status === 'playing' && (Math.abs(b.vy) > 1 || boosted);
        node.scale.setScalar((boosted ? 0.11 : 0.07) * (1 - i / trail.length));
        node.material.emissiveIntensity = boosted ? 0.85 : 0.55;
      }
      renderer.render(scene, camera);
      return !disposed && contextAvailable();
    } catch {
      report('render');
      return false;
    }
  }
  function projectBob() {
    if (disposed || !lastState || !bob) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const centre = bob.root.position.clone().project(camera);
    const edge = bob.root.position
      .clone()
      .add(new THREE.Vector3(BOB_PHYSICS.radius, 0, 0))
      .project(camera);
    return {
      x: rect.left + ((centre.x + 1) * rect.width) / 2,
      y: rect.top + ((1 - centre.y) * rect.height) / 2,
      radius: (Math.abs(edge.x - centre.x) * rect.width) / 2,
    };
  }
  /** Project an immutable domain contact through the currently rendered camera.
   * Positions are world metres; results are client CSS pixels. Offscreen or
   * invalid sources return null instead of becoming a misleading centre toast. */
  function projectEvent(point) {
    if (disposed || !lastState || ![point?.x, point?.y, point?.z].every(Number.isFinite))
      return null;
    const rect = canvas.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) return null;
    camera.updateMatrixWorld(true);
    const projected = new THREE.Vector3(point.x, point.y, point.z).project(camera);
    if (Math.abs(projected.x) > 1 || Math.abs(projected.y) > 1 || Math.abs(projected.z) > 1)
      return null;
    return {
      x: rect.left + ((projected.x + 1) * rect.width) / 2,
      y: rect.top + ((1 - projected.y) * rect.height) / 2,
    };
  }
  async function prepare(state) {
    if (!draw(state, 0, reducedMotion, { phase: 'preparing', seconds: 0 }))
      throw new Error('Bob graphics unavailable.');
    const version = worldVersion,
      request = textureRequest;
    // Network deadlines are independent of animation: a stalled image settles
    // to the actual colour/scenery fallback before gameplay can begin.
    if (!backdropTexture && !backdropTimer) {
      backdropTimer = setTimeout(() => {
        if (disposed || request !== textureRequest) return;
        textureRequest++;
        disposeTexture(pendingTexture);
        pendingTexture = null;
        if (canvas.dataset) canvas.dataset.backdrop = 'fallback';
        settleBackdrop(false);
        onInvalidate();
      }, 20000);
      backdropTimer.unref?.();
    }
    const [character, crate, backdrop] = await Promise.all([
      art?.ready ?? false,
      courseWindow?.ready ?? false,
      backdropReady,
    ]);
    if (disposed || worldVersion !== version) throw new Error('Bob preparation cancelled.');
    clearTimeout(backdropTimer);
    backdropTimer = null;
    return {
      character,
      crate,
      backdrop,
      durations: {
        intro: art?.presentationDuration?.('intro') || 1.8,
        reward: art?.presentationDuration?.('reward') || 0.9,
      },
    };
  }
  return { draw, resize, projectBob, projectEvent, prepare, dispose };
}
